import React, { useState, useRef, useEffect } from "react";
import SendIcon from "img/genai/Send.svg";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import styled from "styled-components";

const GradientInputField = ({
  onSend,
  placeholder = "Type to chat with me !",
  setUserText,
  isMultiline,
  setIsMultiline,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const textareaRef = useRef(null);

  // Check if textarea needs scrollbar
  useEffect(() => {
    const checkScrollbar = () => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        // Check if scroll height is greater than client height
        const needsScrollbar = textarea.scrollHeight > textarea.clientHeight;
        setHasScrollbar(needsScrollbar);
      }
    };

    // Check initially
    checkScrollbar();

    // Use MutationObserver to detect content changes
    const observer = new MutationObserver(checkScrollbar);
    if (textareaRef.current) {
      observer.observe(textareaRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Also check on window resize
    window.addEventListener("resize", checkScrollbar);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkScrollbar);
    };
  }, [inputValue, isMultiline]);

  const handleSubmit = () => {
    if (inputValue.trim()) {
      const msg = inputValue.trim();
      setUserText(msg);
      onSend(msg);
      setInputValue("");
      setHasScrollbar(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const hasActualNewlines = value.includes("\n") || value.includes("\r");
    const isLongText = value.length > 161;

    const isMultilineMode = hasActualNewlines || isLongText;
    setIsMultiline(isMultilineMode);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <Container>
      <InputWrapper $isMultiline={isMultiline}>
        <GradientBorderWrapper $hasScrollbar={hasScrollbar && isMultiline}>
          <StyledTextareaAutosize
            ref={textareaRef}
            placeholder={isFocused ? "" : placeholder}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            minRows={1}
            maxRows={3}
            $isMultiline={isMultiline}
            $hasScrollbar={hasScrollbar && isMultiline}
            className={hasScrollbar && isMultiline ? "customScrollBar" : ""}
          />
          <SendButton
            $ismultiline={isMultiline}
            $hasScrollbar={hasScrollbar}
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
          >
            <img src={SendIcon} alt="Send" />
          </SendButton>
        </GradientBorderWrapper>
      </InputWrapper>
    </Container>
  );
};

// CSS
const Container = styled.div`
  display: flex;
  justify-content: center;
`;

const InputWrapper = styled.div`
  width: 713px;
  min-height: 59px;
  border-radius: 9px;
  background: linear-gradient(
    169.35deg,
    #5d2cc9 -2.36%,
    #00c0f3 28%,
    rgba(158, 120, 244, 0.596) 65%,
    #c51f3f 91%,
    #5d2cc9 160%
  );
  padding: 1px;
  display: flex;
  margin-top: auto;
  align-self: flex-end;
  margin-bottom: ${({ $isMultiline }) => ($isMultiline ? "11px" : "0")};
`;

const GradientBorderWrapper = styled.div`
  width: 100%;
  min-height: 59px;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  position: relative;
  padding: 0 ${({ $hasScrollbar }) => ($hasScrollbar ? "30px" : "50px")} 0 20px;
  box-sizing: border-box;
  align-items: flex-end;
  height: auto;
  min-height: 57px;

  ${({ $isMultiline }) =>
    $isMultiline &&
    `
    padding-top: 12px;
    padding-bottom: 12px;
  `}
`;

const StyledTextareaAutosize = styled(TextareaAutosize)`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: #1e1e1e;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  padding: 0;
  margin: 0;
  line-height: 18px;
  min-height: 42px;
  padding-top: 10px;
  max-height: 72px;
  margin-right: 12px;
  padding-right: 12px;
  /* Only use overflow-y auto when multiline and has scrollbar */
  overflow-y: ${({ $isMultiline, $hasScrollbar }) =>
    $isMultiline && $hasScrollbar ? "auto" : "hidden"} !important;

  /* Remove all inline scrollbar styling - rely only on .customScrollBar class */

  align-self: center;

  &::placeholder {
    color: #878888;
    opacity: 1;
    font-weight: 600;
  }

  &:focus {
    outline: none;
  }

  /* When content exceeds single line, adjust alignment */
  &:not(:empty) {
    align-self: flex-start;
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const SendButton = styled.button`
  background: transparent;
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 12px;
  bottom: ${({ $ismultiline }) => ($ismultiline ? "24px" : "17px")};
  height: 20px;
  width: 20px;

  &:focus {
    outline: none;
  }

  img {
    width: 20px;
    height: 20px;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    transition: opacity 0.2s ease;
    filter: ${(props) =>
      props.disabled ? "grayscale(1) brightness(0.6)" : "none"};
  }

  &:hover:not(:disabled) img {
    opacity: 1;
  }
`;

export default GradientInputField;
